import { Button } from '@askable/ui/core/button';
import { datadogRum } from '@datadog/browser-rum';
import { showNewMessage, hide } from '@intercom/messenger-js-sdk';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'src/lib/routing';

import type { FC, PropsWithChildren } from 'react';

type Props = {
  // Define the error boundary location
  location: string;
};

export const ErrorBoundaryPssTable: FC<PropsWithChildren<Props>> = ({ children }) => {
  const navigate = useNavigate();

  const fallback = () => {
    const ddContext = datadogRum.getInternalContext();

    const handleReload = () => {
      hide();
      // @ts-expect-error TANSTACK_MIGRATION: numeric delta navigation (reload) has no shim equivalent yet
      navigate(0);
    };

    const handleSupportClick = () => {
      showNewMessage(`I'm experiencing an issue with my booking.\n\n Event ID: ${ddContext?.view?.id}`);
    };

    return (
      <div className="mt-40 flex flex-col items-center gap-8">
        <h2 className="text-2xl font-semibold">An unknown error has occurred</h2>
        <div className="flex flex-col items-center gap-1">
          <p>Try refreshing your browser.</p>
          <p className="flex items-center">
            If this doesn't fix your issue{' '}
            <Button variant="link" size="lg" className="-ml-2" onClick={handleSupportClick}>
              contact support.
            </Button>
          </p>
        </div>
        <Button onClick={handleReload} variant="primary">
          Refresh browser
        </Button>
      </div>
    );
  };

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        datadogRum.addError(error, {
          source: 'pss-table',
          type: 'error-boundary',
          errorInfo: errorInfo.componentStack,
        });
        datadogRum.startSessionReplayRecording();
      }}
      fallbackRender={fallback}
    >
      {children}
    </ErrorBoundary>
  );
};
